import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useLocation, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import { GemCertificate } from '../../../../utils/enums/gem-certification.enum';
import { GemClarity } from '../../../../utils/enums/gem-clarity.enum';
import { GemColor } from '../../../../utils/enums/gem-color.enum';
import { GemCut } from '../../../../utils/enums/gem-cut.enum';
import { GemLocation } from '../../../../utils/enums/gem-location.enum';
import { GemOrigin } from '../../../../utils/enums/gem-origin.enum';
import { GemShape } from '../../../../utils/enums/gem-shape.enum';
import { GemSpecies } from '../../../../utils/enums/gem-species.enum';
import { GemTreatment } from '../../../../utils/enums/gem-treatment.enum';
import { Intensity } from '../../../../utils/enums/intensity.enum';
import { gemActions, GemVaultType, IGemCreateFormValues } from '../../../../redux/gem/gemSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { RootState } from '../../../../redux/store';
import { getTitle, Titles } from '../../../../utils/general-layout-util';
import { ActionStatusEnum } from '../../../../utils/enums/action-status.enum';
import useEnv from '../../../../utils/use-env-hook';
import { SpecialDescriptor } from '../../../../utils/enums/gem-special-descriptor.enum';
import { validation } from '../../../../schema/validation';
import { specialDescriptorMap } from '../../../../utils/specialDescriptorMap';

const initialValues: IGemCreateFormValues = {
  species: '',
  weight: '',
  color: '',
  intensity: '',
  secondaryColor: '',
  specialDescriptor: '',
  length: '',
  width: '',
  depth: '',
  cut: '',
  clarity: '',
  treatment: '',
  shape: '',
  origin: '',
  location: '',
  certificate: '',
  file: null,
};

const useUploadGemModal = () => {
  const dispatch = useAppDispatch();
  const gemToEdit = useAppSelector((state: RootState) => state.gem.gemToEdit);
  const componentStatus = useAppSelector((state: RootState) => state.gem.componentStatus);
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const firstPathPart = `/${pathParts[1]}`;
  const title = getTitle(firstPathPart);
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
  const currentLimit = parseInt(searchParams.get('items-per-page') ?? '10', 10);
  const isLoading = _.get(componentStatus, 'addGemLoading', false) as boolean;
  const isModelOpen = _.get(componentStatus, 'isAddGemModelOpen', false) as boolean;
  const addEditGemStatus = _.get(componentStatus, 'addEditGemStatus', ActionStatusEnum.INITIALIZED) as ActionStatusEnum;
  const { baseUrl } = useEnv();
  const [specialDescriptorOptions, setSpecialDescriptorOptions] = useState<SpecialDescriptor[]>([]);
  const [currentColorField, setCurrentColorField] = useState<'primary' | 'secondary' | null>(null);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [isShapeModalOpen, setIsShapeModalOpen] = useState(false);
  const [enableLocationValidation, setEnableLocationValidation] = useState(false);
  const originalValues = useMemo(() => {
    if (!gemToEdit) return null;

    return {
      ...gemToEdit,
      width: gemToEdit.width?.toString() ?? '',
      length: gemToEdit.length?.toString() ?? '',
      depth: gemToEdit.depth?.toString() ?? '',
      weight: gemToEdit.weight?.toString() ?? '',
      file: null,
    };
  }, [gemToEdit]);

  const normalizeValues = (values: any) => {
    const clone = { ...values };

    Object.keys(clone).forEach((key) => {
      if (clone[key] === undefined || clone[key] === null) {
        clone[key] = '';
      } else if (typeof clone[key] === 'number') {
        clone[key] = clone[key].toString();
      }
    });

    if ('file' in clone) clone.file = null;

    return clone;
  };

  const getChangedValues = (current: any, original: any) => {
    const changed: Record<string, any> = {};

    Object.keys(current).forEach((key) => {
      if (!_.isEqual(current[key], original[key])) {
        changed[key] = current[key];
      }
    });

    return changed;
  };

  const formik = useFormik({
    initialValues,
    validationSchema: yup.object(
      gemToEdit
        ? {
            species: validation.uploadGemFieldValidation,
            cut: validation.uploadGemFieldValidation,
            weight: validation.weight,
            color: validation.uploadGemFieldValidation,
            secondaryColor: validation.uploadGemFieldValidation,
            specialDescriptor:
              specialDescriptorOptions?.length > 0 ? validation.uploadGemFieldValidation : yup.string().nullable(),
            length: validation.dimensions,
            width: validation.dimensions,
            shape: validation.uploadGemFieldValidation,
            depth: validation.dimensions,
            clarity: validation.uploadGemFieldValidation,
            treatment: validation.uploadGemFieldValidation,
            certificate: validation.uploadGemFieldValidation,
            origin: validation.uploadGemFieldValidation,
            location: enableLocationValidation ? validation.uploadGemFieldValidation : yup.string().default(''),
          }
        : {
            species: validation.uploadGemFieldValidation,
            weight: validation.weight,
            color: validation.uploadGemFieldValidation,
            secondaryColor: validation.uploadGemFieldValidation,
            specialDescriptor:
              specialDescriptorOptions?.length > 0 ? validation.uploadGemFieldValidation : yup.string().default(''),
            length: validation.dimensions,
            width: validation.dimensions,
            depth: validation.dimensions,
            cut: validation.uploadGemFieldValidation,
            shape: validation.uploadGemFieldValidation,
            clarity: validation.uploadGemFieldValidation,
            treatment: validation.uploadGemFieldValidation,
            certificate: validation.uploadGemFieldValidation,
            origin: validation.uploadGemFieldValidation,
            location: enableLocationValidation ? validation.uploadGemFieldValidation : yup.string().default(''),
            file: gemToEdit ? yup.mixed().nullable() : validation.file,
          },
    ),
    onSubmit: (values) => {
      const cleanValues = { ...values };

      if (cleanValues.intensity === '') {
        delete cleanValues.intensity;
      }
      if (cleanValues.location === '') {
        delete cleanValues.location;
      }

      const original = normalizeValues(originalValues || {});
      const current = normalizeValues(_.pick(values, Object.keys(original)));

      const changedValues = getChangedValues(current, original);

      // No need to submit if nothing has changed
      if (gemToEdit && Object.keys(changedValues).length === 0) return;

      if (gemToEdit) {
        if (title === Titles.MyGemVault) {
          dispatch(
            gemActions.editGemAsync({
              gem: changedValues,
              gemId: gemToEdit.gemId,
              type: GemVaultType.GEM_VAULT,
              currentPage,
              currentLimit,
            }),
          );
        }
        if (title === Titles.MyGemFav) {
          dispatch(
            gemActions.editGemAsync({
              gem: changedValues,
              gemId: gemToEdit.gemId,
              type: GemVaultType.MY_GEM_FAV,
              currentPage,
              currentLimit,
            }),
          );
        }
        if (title === Titles.GemBank) {
          dispatch(
            gemActions.editGemAsync({
              gem: changedValues,
              gemId: gemToEdit.gemId,
              type: GemVaultType.GEM_BANK,
              currentPage,
              currentLimit,
            }),
          );
        }
      } else if (cleanValues.file) {
        const type = (() => {
          switch (title) {
            case Titles.MyGemVault:
              return GemVaultType.GEM_VAULT;
            case Titles.MyGemFav:
              return GemVaultType.MY_GEM_FAV;
            case Titles.GemBank:
              return GemVaultType.GEM_BANK;
            default:
              return GemVaultType.GEM_VAULT;
          }
        })();

        dispatch(
          gemActions.addGemAsync({
            gem: cleanValues,
            image: cleanValues.file,
            type,
          }),
        );
      }
    },
  });

  const isFormChanged = useMemo(() => {
    if (!gemToEdit || !originalValues) return false;

    const relevantKeys = Object.keys(originalValues);
    const current = normalizeValues(_.pick(formik.values, relevantKeys));
    const original = normalizeValues(originalValues);

    return !_.isEqual(current, original);
  }, [formik.values, originalValues, gemToEdit]);

  useEffect(() => {
    if (formik.values.origin === GemOrigin.SriLanka) {
      setEnableLocationValidation(true);
    } else {
      formik.setFieldValue('location', '');
      setEnableLocationValidation(false);
    }
  }, [formik.values.origin]);

  useEffect(() => {
    formik.setValues(
      gemToEdit
        ? {
            ...gemToEdit,
            width: gemToEdit.width?.toString(),
            length: gemToEdit.length?.toString(),
            depth: gemToEdit.depth?.toString(),
            weight: gemToEdit.weight?.toString(),
            file: null,
          }
        : initialValues,
    );
  }, [gemToEdit]);

  useEffect(() => {
    if (addEditGemStatus === ActionStatusEnum.SUCCESS) {
      formik.resetForm();
      formik.setValues(initialValues);
    }
  }, [addEditGemStatus]);

  const handleFileDrop = (acceptedFiles: File[]) => {
    formik.setTouched({ ...formik.touched, file: true });
    formik.setFieldValue('file', acceptedFiles[0] || null, true);
  };
  const handleRemoveFile = () => {
    formik.setTouched({ ...formik.touched, file: true });
    formik.setFieldValue('file', null, true);
  };

  const handleCloseModel = () => {
    if (!isLoading) {
      formik.resetForm();
      if (gemToEdit) {
        dispatch(gemActions.closeGemModel({ gemId: gemToEdit.gemId }));
      } else {
        dispatch(gemActions.closeGemModel({ gemId: null }));
      }
    }
    setSpecialDescriptorOptions([]);
  };

  const handleDeleteGem = () => {
    dispatch(gemActions.openDeleteGemModel());
  };

  const createDropdownOptions = (enumObj: Record<string, string>) =>
    Object.values(enumObj).map((option) => ({ label: option, value: option }));

  const dropdownOptions = useMemo(
    () => ({
      species: createDropdownOptions(GemSpecies),
      color: createDropdownOptions(GemColor),
      intensity: createDropdownOptions(Intensity),
      secondaryColor: createDropdownOptions(GemColor),
      cut: createDropdownOptions(GemCut),
      clarity: createDropdownOptions(GemClarity),
      treatment: createDropdownOptions(GemTreatment),
      shape: createDropdownOptions(GemShape),
      origin: createDropdownOptions(GemOrigin),
      location: createDropdownOptions(GemLocation),
      certificate: createDropdownOptions(GemCertificate),
    }),
    [],
  );

  const getAppearanceImage = (): string | undefined => {
    if (gemToEdit) {
      return `${baseUrl}/${gemToEdit.appearanceImage}`;
    }
    if (formik.values.file) {
      return URL.createObjectURL(formik.values.file);
    }
    return undefined;
  };

  const handlePrimarySecondaryColorChange = (primaryColor: GemColor, secondaryColor: GemColor) => {
    const secondaryOptions = specialDescriptorMap[primaryColor]?.[secondaryColor || GemColor.Nil] || [];
    setSpecialDescriptorOptions(secondaryOptions);

    if (secondaryOptions.length === 0) {
      formik.setFieldValue('specialDescriptor', '');
    }
  };

  const handleOpenColorModal = (field: 'primary' | 'secondary') => {
    setCurrentColorField(field);
    setIsColorModalOpen(true);
  };

  const handleOpenShapeModal = () => {
    setIsShapeModalOpen(true);
  };

  const handleSelectShape = (shape: GemShape) => {
    formik.setFieldValue('shape', shape);
    setCurrentColorField(null);
  };

  const handleSelectColor = (color: GemColor) => {
    if (currentColorField === 'primary') {
      formik.setFieldValue('color', color);
      formik.setFieldTouched('color', false);
      handlePrimarySecondaryColorChange(color, formik.values.secondaryColor as GemColor);
    } else if (currentColorField === 'secondary') {
      formik.setFieldValue('secondaryColor', color);
      formik.setFieldTouched('secondaryColor', false);
      handlePrimarySecondaryColorChange(formik.values.color as GemColor, color);
    }
    setCurrentColorField(null);
  };

  useEffect(() => {
    if (!formik.values.specialDescriptor) return;

    setSpecialDescriptorOptions((prevOptions: any[]) => {
      if (!prevOptions.includes(formik.values.specialDescriptor as SpecialDescriptor)) {
        return [...prevOptions, formik.values.specialDescriptor];
      }
      return prevOptions;
    });
  }, [formik.values.specialDescriptor]);

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      formik.handleSubmit();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, []);

  return {
    isModelOpen,
    gemToEdit,
    handleCloseModel,
    formik,
    isColorModalOpen,
    handleOpenColorModal,
    dropdownOptions,
    specialDescriptorOptions,
    isShapeModalOpen,
    handleOpenShapeModal,
    handleFileDrop,
    getAppearanceImage,
    handleRemoveFile,
    addEditGemStatus,
    handleDeleteGem,
    isFormChanged,
    setIsColorModalOpen,
    handleSelectColor,
    currentColorField,
    setIsShapeModalOpen,
    handleSelectShape,
  };
};

export default useUploadGemModal;
